// TeamMemberRow — one membership row in the admin Team Mapping table
// (PRODOPSAI-539). Columns: Name | Team role | AD/Entra title | Actions.
//
// Edit (pencil) opens the EditMembershipPopover (change role / move team /
// edit note). Remove is a first-class trashcan with an inline two-step confirm
// (delete is irreversible), so removal has a single, obvious path.

import { useCallback, useState } from 'react';
import { Badge, Button, Tooltip, TrashIcon } from '@/components/ui';
import { cn, getErrorMessage } from '@/lib/utils';
import type {
  ITeamMembershipTreePerson,
  ITeamMembershipTreeRoleGroup,
} from '@skystone/shared';
import { teamMembershipApi } from '@/services/team-membership-api';
import { PersonHoverCard } from '@/components/people/PersonHoverCard';
import { EditMembershipPopover } from './EditMembershipPopover';
import { MEMBER_ROW_GRID, personDisplayProps, toHoverCardNode } from './team-role-tree-helpers';

interface TeamMemberRowProps {
  person: ITeamMembershipTreePerson;
  teamId: string;
  teamName: string;
  group: ITeamMembershipTreeRoleGroup;
  onMembershipChanged?: (message: string) => void;
}

export function TeamMemberRow({
  person,
  teamId,
  teamName,
  group,
  onMembershipChanged,
}: Readonly<TeamMemberRowProps>) {
  const display = personDisplayProps(person);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const isEntraTitle = group.source === 'entra-title';
  const adTitle = person.jobTitle?.trim() ?? '';

  const handleChanged = (message: string) => {
    setPopoverOpen(false);
    onMembershipChanged?.(message);
  };

  const handleRemove = useCallback(async () => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await teamMembershipApi.deleteTeamMembership(person.membershipId);
      // Reset locally too: the parent normally refetches and unmounts this row,
      // but `onMembershipChanged` is optional, so never leave the row stuck.
      setConfirming(false);
      setRemoving(false);
      onMembershipChanged?.(`Removed ${person.displayName} from ${teamName}`);
    } catch (err: unknown) {
      setRemoveError(getErrorMessage(err, 'Failed to remove mapping'));
      setRemoving(false);
    }
  }, [person.membershipId, person.displayName, teamName, onMembershipChanged]);

  return (
    <div
      className={cn(MEMBER_ROW_GRID, 'rounded-md px-2 py-1.5 text-sm hover:bg-muted/50')}
      data-testid="member-row"
      data-membership-id={person.membershipId}
    >
      <div className="flex min-w-0 items-center gap-2">
        {/* PRODOPSAI-520: click the name to open the PersonHoverCard (Entra
            metadata). The departed-person Tooltip wraps it on the outside so the
            hover hint is preserved for inactive members. Tooltip is a CSS
            group-hover wrapper (no ref forwarding), so it must sit OUTSIDE the
            Radix Popover trigger; the trigger itself is a <button> (S6847). */}
        <Tooltip content={display.tooltipContent ?? ''} disabled={display.tooltipContent === null} side="top">
          <PersonHoverCard node={toHoverCardNode(person)} departmentLabel={person.department}>
            <button
              type="button"
              className={cn('min-w-0 truncate text-left', display.nameClassName)}
              data-testid="member-name-trigger"
              aria-label={`Details for ${person.displayName}`}
            >
              {person.displayName}
            </button>
          </PersonHoverCard>
        </Tooltip>
        {person.note !== null && person.note.trim() !== '' && (
          <Badge variant="secondary" className="text-[10px]" data-testid="member-row-note">
            {person.note}
          </Badge>
        )}
      </div>

      <div className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
        <span className="truncate">{group.displayName}</span>
        {isEntraTitle && (
          <Badge variant="secondary" className="text-[10px]" data-testid="member-row-from-entra">
            from Entra
          </Badge>
        )}
      </div>

      <div className="min-w-0 truncate text-muted-foreground" data-testid="member-row-ad-title">
        {adTitle === '' ? '—' : adTitle}
      </div>

      <div className="relative flex items-center justify-end gap-1">
        {confirming ? (
          <div className="flex items-center gap-1" data-testid="member-row-confirm-remove">
            <span className="text-xs text-muted-foreground">Remove?</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={removing}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => handleRemove().catch(() => undefined)}
              isLoading={removing}
              data-testid="member-row-remove-confirm"
            >
              Remove
            </Button>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setPopoverOpen((prev) => prev === false)}
              className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={`Edit membership for ${person.displayName}`}
              aria-expanded={popoverOpen}
              title="Edit membership"
              data-testid="edit-membership-trigger"
            >
              {'⋯'}
            </button>
            <button
              type="button"
              onClick={() => {
                setRemoveError(null);
                setPopoverOpen(false);
                setConfirming(true);
              }}
              className="rounded-md p-1 text-muted-foreground hover:bg-error/10 hover:text-error"
              aria-label={`Remove ${person.displayName} from ${teamName}`}
              title="Remove from team"
              data-testid="member-row-remove"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </>
        )}
        {popoverOpen === true && (
          <EditMembershipPopover
            membership={{
              person,
              teamId,
              teamName,
              role: {
                roleId: group.roleId,
                roleLabel: group.displayName,
                useEntraTitle: isEntraTitle,
                personJobTitle: person.jobTitle,
              },
            }}
            onChanged={handleChanged}
            onClose={() => setPopoverOpen(false)}
          />
        )}
      </div>

      {removeError !== null && (
        <p className="col-span-full text-xs text-error" role="alert">
          {removeError}
        </p>
      )}
    </div>
  );
}
